import { environmentVars } from 'constants/services';
import jwtDecode from 'jwt-decode';
import { History } from 'history';
import qs from 'qs';
import {
  areCredentialsValid,
  convertTokensToCredentials,
  getClaims,
} from '../credentials';
import { generateNonce } from '../nonce';
import { decodeState, encodeState, generateState } from '../state';

export class BbIdImplicitFlowAuthService
  implements Bluebeam.Services.IAuthService
{
  private _credentials: Bluebeam.Auth.Models.Credentials | undefined =
    undefined;

  constructor(
    private readonly _history: History,
    private readonly _deviceStorageService: Bluebeam.Services.IDeviceStorageService
  ) {}

  getCredentials: Bluebeam.Services.IAuthService['getCredentials'] = () => {
    if (!this._credentials) {
      throw new Error('The user is currently logged out.');
    }

    return this._credentials;
  };

  isLoggedIn: Bluebeam.Services.IAuthService['isLoggedIn'] = () => {
    return Boolean(this._credentials);
  };

  isRedirect: Bluebeam.Services.IAuthService['isRedirect'] = () => {
    return Promise.resolve(
      window.location.hash.includes('access_token=') &&
        window.location.hash.includes('id_token=')
    );
  };

  hasExternalCredentials: Bluebeam.Services.IAuthRefreshableService['hasExternalCredentials'] =
    () => {
      return Promise.resolve(false);
    };

  loginWithExternalCredentials: Bluebeam.Services.IAuthRefreshableService['loginWithExternalCredentials'] =
    () => {
      return Promise.resolve();
    };

  login: Bluebeam.Services.IAuthService['login'] = async () => {
    /* eslint-disable camelcase */

    const {
      access_token,
      id_token,
      state: encodedState,
    } = qs.parse(window.location.hash.slice(1));

    if (typeof access_token !== 'string') {
      return Promise.reject(new Error('The access token is invalid'));
    }

    if (typeof id_token !== 'string') {
      return Promise.reject(new Error('The id token is invalid'));
    }

    if (typeof encodedState !== 'string') {
      return Promise.reject(new Error('The state is invalid'));
    }

    const state = decodeState(encodedState);
    const { nonce: nonceFromToken } = jwtDecode<{ nonce: string }>(id_token);
    const nonceForRedirectId = await this._deviceStorageService.getItem(
      `bbid_nonce_${state.redirectId}`
    );

    if (nonceFromToken !== nonceForRedirectId) {
      return Promise.reject(new Error('The nonce values must match'));
    }

    const credentials = convertTokensToCredentials(access_token, id_token);

    /* eslint-enable camelcase */

    areCredentialsValid(credentials);

    const issuer = getClaims(credentials.accessToken).iss;
    this._deviceStorageService.setItem('bbid_issuer', issuer);

    this._credentials = credentials;

    this._history.replace(`${state.pathName}${state.search ?? ''}`);
    return Promise.resolve();
  };

  logout: Bluebeam.Services.IAuthService['logout'] = async () => {
    const logoutQuery = qs.stringify({
      client_id: environmentVars.BB_OPENID_CLIENT_ID,
      id_token_hint: this._credentials?.idToken,
      post_logout_redirect_uri: `${window.location.origin}`,
    });

    this._credentials = undefined;

    this._deviceStorageService.removeItem('bbid_issuer');

    window.location.assign(
      `${environmentVars.BB_OPENID_ENDPOINT}/connect/endsession?${logoutQuery}`
    );
  };

  redirect: Bluebeam.Services.IAuthService['redirect'] = async () => {
    const nonce = generateNonce();
    const state = generateState();

    await this._deviceStorageService.setItem(
      `bbid_nonce_${state.redirectId}`,
      nonce
    );

    let redirectUrl = `${environmentVars.BB_OPENID_ENDPOINT}/connect/authorize`;

    const issuer = await this._deviceStorageService.getItem<string>(
      'bbid_issuer'
    );

    if (issuer && issuer.length > 0) {
      const issuerUrl = new URL(issuer);
      if (
        issuerUrl &&
        (issuerUrl.hostname.toLowerCase().includes('okta') ||
          issuerUrl.hostname.toLowerCase().includes('signin.bluebeam'))
      ) {
        redirectUrl = `${issuerUrl}/v1/authorize`;
      }
    }

    const redirectQueryString = qs.stringify({
      client_id: environmentVars.BB_OPENID_CLIENT_ID,
      nonce,
      redirect_uri: `${window.location.origin}/auth`,
      response_mode: 'fragment',
      response_type: 'token id_token',
      scope: 'openid profile email',
      state: encodeState(state),
    });

    window.location.assign(`${redirectUrl}?${redirectQueryString}`);

    return Promise.resolve();
  };

  setCredentials: Bluebeam.Services.IAuthService['setCredentials'] = (
    tokens
  ) => {
    const credentials = convertTokensToCredentials(
      tokens.accessToken,
      tokens.idToken
    );

    areCredentialsValid(credentials);

    this._credentials = credentials;
  };

  areCredentialsExpired: Bluebeam.Services.IAuthService['areCredentialsExpired'] =
    () => {
      if (!this._credentials) {
        throw new Error('The user is currently logged out.');
      }

      const claims = getClaims(this._credentials.accessToken);

      if (!claims.exp || typeof claims.exp !== 'number') {
        throw new Error("The access token doesn't have an expiration time");
      }

      const secondsUntilExpiration = claims.exp - Math.floor(Date.now() / 1000);

      return secondsUntilExpiration <= 0;
    };
}
